import React, { Suspense, lazy, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetProgramCategoriesMetricsQuery } from "../../features/program/programApi.services";
import UserBreakdownChart from "./Admin/UserBreakdownChart";
import ProgramMetrix from "./ProgramMetrix";
import MapSkeleton from "../../shared/Skeleton/MapSkeleton/MapSkeleton";
import ProgramChartMetricsSkeleton from "../../shared/Skeleton/AdminSkeleton/ProgramChartMetricsSkeleton";

const GoogleMapsComponent = lazy(() => import("../../shared/Map/Map"));
const ProgramCategoryChart = lazy(() => import("./Admin/ProgramCategoryChart"));
const ProgramModeChart = lazy(() => import("./Admin/ProgramModeChart"));

const CARD_STYLE = {
  boxShadow: "4px 4px 25px 0px rgba(0, 0, 0, 0.05)",
  background: "rgba(255, 255, 255, 1)",
};

export default function Admin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterType = searchParams.get("type");

  const [programMatrixLoading, setProgramMatrixLoading] = useState(true);
  const [programCategoriesYear, setProgramCategoriesYear] = useState(null);
  const [programCategoriesMonth, setProgramCategoriesMonth] = useState(null);

  const programCategoriesFilters = {};
  if (programCategoriesYear) {
    programCategoriesFilters.started_year = programCategoriesYear;
  }
  if (programCategoriesMonth) {
    programCategoriesFilters.started_month = programCategoriesMonth;
  }

  const {
    data: programCategoriesMetrics,
    isLoading: isLoadingCategoriesMetrics,
    isFetching: isFetchingCategoriesMetrics,
  } = useGetProgramCategoriesMetricsQuery(
    Object.keys(programCategoriesFilters).length
      ? programCategoriesFilters
      : undefined,
    { refetchOnMountOrArgChange: true }
  );

  // Reset scroll position when the dashboard mounts.
  useEffect(() => {
    window.scrollTo(0, 0);
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  }, []);


  // Default program matrix tab to "yettojoin" while preserving other query params.
  useEffect(() => {
    if (filterType) return;

    const newSearchParams = new URLSearchParams(searchParams);
    for (const [key, value] of searchParams.entries()) {
      newSearchParams.set(key, value);
    }
    newSearchParams.set("type", "yettojoin");
    setSearchParams(newSearchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dashboard-content px-2 sm:px-2 md:px-4 lg:px-6  mt-0 sm:mt-0 md:mt-3 lg:mt-6 mb-2 w-full min-w-0 overflow-x-hidden">
      <div
        className="mb-4 w-full overflow-hidden rounded-lg bg-white"
        style={CARD_STYLE}
      >
        <Suspense fallback={<MapSkeleton />}>
          <GoogleMapsComponent />
        </Suspense>
      </div>

      <div className="grid grid-cols-10 gap-x-4 gap-y-4 max-md:block max-md:space-y-6">
        <div className="col-span-4 w-full">
          <div
            className="w-full overflow-hidden rounded-lg bg-white"
            style={CARD_STYLE}
          >
            <UserBreakdownChart />
          </div>
        </div>

        <div
          className="col-span-6 h-full w-full rounded-lg bg-white"
          style={CARD_STYLE}
        >
          <ProgramMetrix
            onLoadingChange={setProgramMatrixLoading}
            isLoading={programMatrixLoading}
            showFiltericon
          />
        </div>

        <div className="col-span-5 rounded-lg bg-white" style={CARD_STYLE}>
          <Suspense fallback={<ProgramChartMetricsSkeleton />}>
            <ProgramCategoryChart
              programMetrics={programCategoriesMetrics}
              isFetching={isFetchingCategoriesMetrics}
              isLoading={isLoadingCategoriesMetrics}
              isMonthFilter={setProgramCategoriesMonth}
              isYearFilter={setProgramCategoriesYear}
            />
          </Suspense>
        </div>

        <div className="col-span-5 rounded-lg bg-white" style={CARD_STYLE}>
          <Suspense fallback={<ProgramChartMetricsSkeleton />}>
            <ProgramModeChart />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
